/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useEffect, useState } from "react";
import { fetcher } from "../lib/react-query/fetcher";
import { useConfig } from "../lib/react-query/useConfig";

/**
 * Fetches captcha challenge data.
 * Rendering and token management are handled by CaptchaWidget (via SBCaptcha).
 * Returns { challengeData, isReady, refreshChallenge }.
 * Call refreshChallenge() after a failed booking attempt to get a fresh challenge.
 */
export const useCaptcha = () => {
  const { data: config } = useConfig();
  const [challengeData, setChallengeData] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const captchaConfig = config?.captcha;
  const enabled = captchaConfig?.enabled || false;

  useEffect(() => {
    if (!enabled) {
      setChallengeData(null);
      setIsReady(false);
      return;
    }

    setIsReady(false);
    fetcher({ method: "GET", url: "/public/captcha/challenge" })
      .then((data) => {
        setChallengeData(data);
        setIsReady(true);
      })
      .catch(() => {
        setIsReady(false);
      });
  }, [enabled, refreshKey]);

  const refreshChallenge = useCallback(() => setRefreshKey((k) => k + 1), []);

  return { challengeData, isReady, refreshChallenge };
};
